import { useState } from 'react';
import { Container, Title, Button, Group, Stack, Text, Alert, Paper, Badge, Table, ActionIcon, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconInfoCircle, IconX, IconMail } from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useAuth } from '@/hooks/useAuth';
import {
  useOrganizationMembers,
  useRemoveMember,
  useCreateInvite,
  useOrganizationInvites,
  useRevokeInvite,
} from '@/hooks/api/useOrganizationMembers';
import { useCustomRoles } from '@/hooks/api/useCustomRoles';
import { UsersTable } from './components/UsersTable';
import { InviteUserModal } from './components/InviteUserModal';
import { UserPermissionsModal } from './components/UserPermissionsModal';
import type { OrganizationMember, OrganizationInvite } from '@/types/models';

export function UsersPage() {
  const { organizationId, isAdmin } = useOrganization();
  const { user } = useAuth();
  const { data: members = [], isLoading } = useOrganizationMembers(organizationId || '');
  const { data: invites = [] } = useOrganizationInvites(organizationId || '');
  const { data: customRoles = [] } = useCustomRoles(organizationId || '');
  const removeMember = useRemoveMember();
  const createInvite = useCreateInvite();
  const revokeInvite = useRevokeInvite();

  const [inviteOpened, setInviteOpened] = useState(false);
  const [permissionsModalOpened, setPermissionsModalOpened] = useState(false);
  const [selectedMember, setSelectedMember] = useState<OrganizationMember | null>(null);

  const handleInvite = () => {
    setInviteOpened(true);
  };

  const handleInviteSubmit = async (email: string, isAdmin: boolean, customRoleId: string | null) => {
    if (!organizationId || !user?.id) return;

    await createInvite.mutateAsync({
      organizationId,
      email,
      isAdmin,
      customRoleId,
      invitedBy: user.id,
    });

    setInviteOpened(false);
  };

  const handleRevokeInvite = (invite: OrganizationInvite) => {
    modals.openConfirmModal({
      title: 'Cancel Invitation',
      children: (
        <Text size="sm">
          Are you sure you want to cancel the invitation for <strong>{invite.email}</strong>?
        </Text>
      ),
      labels: { confirm: 'Cancel Invitation', cancel: 'Keep Invitation' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        revokeInvite.mutate(invite.id);
      },
    });
  };


  const handleRemove = (member: OrganizationMember) => {
    // Prevent removing yourself
    if (member.user_id === user?.id) {
      notifications.show({
        title: 'Cannot remove yourself',
        message: 'You cannot remove yourself from the organization',
        color: 'red',
      });
      return;
    }

    modals.openConfirmModal({
      title: 'Remove User',
      children: (
        <Text size="sm">
          Are you sure you want to remove <strong>{member.profile?.full_name || member.profile?.email}</strong> from
          the organization? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        removeMember.mutate(member.id);
      },
    });
  };

  const handleManagePermissions = (member: OrganizationMember) => {
    setSelectedMember(member);
    setPermissionsModalOpened(true);
  };

  const handlePermissionsSuccess = () => {
    // Refetch members to get updated permissions
    // The useOrganizationMembers hook will automatically refetch
  };

  // Helper function to get role display info for an invite
  const getInviteRoleDisplay = (invite: OrganizationInvite) => {
    if (invite.is_admin) {
      return { name: 'Admin', color: 'red' };
    }
    if (invite.custom_role_id) {
      const role = customRoles.find((r) => r.id === invite.custom_role_id);
      return { name: role?.name || 'Custom Role', color: 'blue' };
    }
    return { name: 'Member', color: 'gray' };
  };

  // Check if user is admin
  if (!isAdmin) {
    return (
      <Container fluid>
        <Alert icon={<IconInfoCircle size={16} />} color="red">
          <Text size="sm">
            You do not have permission to access this page. Only administrators can manage users.
          </Text>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Stack gap="lg">
        <Group justify="apart">
          <div>
            <Title order={1}>Users</Title>
            <Text c="dimmed" size="sm">
              Manage user access and roles in your organization
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={handleInvite}>
            Invite User
          </Button>
        </Group>

        <Alert icon={<IconInfoCircle size={16} />} color="blue">
          <Text size="sm">
            <strong>Admin</strong>: Full access, bypasses all permissions. <strong>Custom Roles</strong>: Define specific page access and CRUD permissions for non-admin users.
          </Text>
        </Alert>

        {invites.length > 0 && (
          <Paper withBorder p="md">
            <Stack gap="md">
              <Group justify="apart">
                <div>
                  <Title order={3}>Pending Invitations</Title>
                  <Text size="sm" c="dimmed">
                    Users who have been invited but haven't accepted yet
                  </Text>
                </div>
                <Badge size="lg" color="blue" variant="light">
                  {invites.length} pending
                </Badge>
              </Group>

              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Role</Table.Th>
                    <Table.Th>Invited</Table.Th>
                    <Table.Th>Expires</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {invites.map((invite) => {
                    const roleDisplay = getInviteRoleDisplay(invite);
                    return (
                      <Table.Tr key={invite.id}>
                        <Table.Td>
                          <Group gap="xs">
                            <IconMail size={16} />
                            <Text size="sm">{invite.email}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Badge color={roleDisplay.color} variant="light">
                            {roleDisplay.name}
                          </Badge>
                        </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {new Date(invite.created_at).toLocaleDateString()}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {new Date(invite.expires_at).toLocaleDateString()}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label="Cancel invitation">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => handleRevokeInvite(invite)}
                          >
                            <IconX size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Stack>
          </Paper>
        )}

        <UsersTable
          members={members}
          currentUserId={user?.id || ''}
          onRemove={handleRemove}
          onManagePermissions={handleManagePermissions}
          isLoading={isLoading}
        />

        <InviteUserModal
          opened={inviteOpened}
          onClose={() => setInviteOpened(false)}
          onSubmit={handleInviteSubmit}
          isLoading={createInvite.isPending}
        />

        <UserPermissionsModal
          opened={permissionsModalOpened}
          onClose={() => {
            setPermissionsModalOpened(false);
            setSelectedMember(null);
          }}
          member={selectedMember}
          onSuccess={handlePermissionsSuccess}
        />
      </Stack>
    </Container>
  );
}
